import {
  email,
  equalToFieldInsensitive,
  existsInError,
  notEqualTo,
  requiredWithError,
  setError,
} from '@kaa/common/validation';
import { i18nKeys } from '@kaa/i18n/customers/keys';
import {
  SwActionGroup,
  SwButton,
  SwColumn,
  SwForm,
  SwFormColumn,
  SwFormGrid,
  SwFormRequiredLegend,
  SwFormSubmitMessage,
  SwGrid,
  SwInputTextField,
  SwModalRenderProps,
  SwTitle,
} from '@kaa/ui-flanders/components';
import { Field, Formik, FormikActions } from 'formik';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { dataTest } from '../../../../datatest/keys';
import {
  ModalType,
  sendModalInteractionEvent,
} from '../../../../utils/google-analytics';
import { FlowStartWatcher } from '../../../../utils/FlowStartWatcher';
import { createValidatorWithServerErrorHandled } from '../../../../utils/validation';
import { ProfileScreenChangeEmailFieldNames } from '../ProfileScreen.constants';
import { ProfileUserInfoChangeEmailFormState } from '../ProfileScreen.type';

type ConfirmEmailModalProps = {
  userEmailAddress: string;
  onSubmit: (
    data: ProfileUserInfoChangeEmailFormState,
    FormikActions: FormikActions<ProfileUserInfoChangeEmailFormState>,
  ) => Promise<[Error | undefined, any | undefined]>;
};

export const ProfileUserInfoChangeEmailModal = ({
  userEmailAddress,
  onSubmit,
  setConfirmCloseModal,
}: SwModalRenderProps<ConfirmEmailModalProps>) => {
  const { t } = useTranslation();

  const validate = createValidatorWithServerErrorHandled({
    emailAddress: [
      requiredWithError(
        i18nKeys.profile.error.emailAddress.required,
        'Email address is required',
      ),
      email,
      notEqualTo(userEmailAddress),
    ],
    confirmEmailAddress: [
      existsInError(
        `${ProfileScreenChangeEmailFieldNames.CONFIRM_EMAIL_ADDRESS}.notPasted`,
        {
          id: i18nKeys.errors.form.noPaste,
          defaultMessage: 'You cannot paste',
          displayFocussed: true,
        },
      ),
      requiredWithError(
        i18nKeys.profile.error.confirmEmailAddress.required,
        'Email confirmation is required',
      ),
      email,
      equalToFieldInsensitive(
        ProfileScreenChangeEmailFieldNames.EMAIL_ADDRESS,
        {
          defaultMessage: `Must be equal to ${t(
            i18nKeys.general.label.emailAddress,
          )}`,
          id: i18nKeys.errors.form.equal,
          displayFocussed: false,
          values: {
            value: t(i18nKeys.general.label.emailAddress),
          },
        },
      ),
    ],
  });

  return (
    <Formik
      initialValues={{
        emailAddress: '',
        confirmEmailAddress: '',
      }}
      validate={validate}
      onSubmit={(...props) => {
        sendModalInteractionEvent(
          'Submit Change Email Address',
          'Profile Change Email Address Modal',
          ModalType.FORM,
        );
        onSubmit(...props);
      }}
      render={({ handleSubmit, dirty, isSubmitting, setFieldValue }) => {
        setConfirmCloseModal(dirty);

        return (
          <SwGrid>
            <SwColumn width="12">
              <SwTitle tagName="h1" tagStyle="h2">
                {t(i18nKeys.profile.changeEmailModal.title)}
              </SwTitle>
            </SwColumn>
            <SwColumn width="12">
              <SwFormRequiredLegend>
                {t(i18nKeys.forms.requiredLegend)}
              </SwFormRequiredLegend>
            </SwColumn>
            <SwColumn width="12" widthS="12">
              <SwForm
                onSubmit={handleSubmit}
                data-testid={dataTest.profile.changeEmailModal.formChangeEmail}
              >
                <FlowStartWatcher
                  dirty={dirty}
                  formName="change_email"
                  virtualPageUrl="/profile/change-email"
                  options={{ flowName: 'Change Email Address' }}
                />
                <SwFormGrid modStacked>
                  <SwFormColumn>
                    <SwFormGrid>
                      <SwFormColumn width="12">
                        <span className="vl-form__label">
                          {t(i18nKeys.profile.changeEmailModal.oldEmailAddress)}
                        </span>
                      </SwFormColumn>

                      <SwFormColumn
                        width="12"
                        data-testid={
                          dataTest.profile.changeEmailModal.oldEmailAdress
                        }
                      >
                        {userEmailAddress}
                      </SwFormColumn>
                    </SwFormGrid>
                  </SwFormColumn>
                  <Field
                    data-testid={
                      dataTest.profile.changeEmailModal.newEmailAddress
                    }
                    name={ProfileScreenChangeEmailFieldNames.EMAIL_ADDRESS}
                    column={{ width: '12' }}
                    label={t(i18nKeys.general.label.emailAddress)}
                    labelColumn={{ width: '12' }}
                    component={SwInputTextField}
                    type="email"
                    autoComplete="email"
                    modRequired
                  />
                  <Field
                    data-testid={
                      dataTest.profile.changeEmailModal.confirmEmailAddress
                    }
                    name={
                      ProfileScreenChangeEmailFieldNames.CONFIRM_EMAIL_ADDRESS
                    }
                    column={{ width: '12' }}
                    label={t(i18nKeys.profile.emailAddressConfirmation)}
                    labelColumn={{ width: '12' }}
                    component={SwInputTextField}
                    type="email"
                    modRequired
                    onPaste={(e: ClipboardEvent) => {
                      e.preventDefault();
                      setError({
                        setFieldValue,
                        name: `${ProfileScreenChangeEmailFieldNames.CONFIRM_EMAIL_ADDRESS}.notPasted`,
                      });
                    }}
                  />
                  <SwActionGroup>
                    <SwButton
                      data-testid={
                        dataTest.profile.changeEmailModal.submitChangeEmailModal
                      }
                      type="submit"
                      modDisabled={!dirty}
                      modLoading={isSubmitting}
                    >
                      {t(i18nKeys.general.cta.validate)}
                    </SwButton>
                  </SwActionGroup>
                  <SwFormSubmitMessage />
                </SwFormGrid>
              </SwForm>
            </SwColumn>
          </SwGrid>
        );
      }}
    />
  );
};
